import type { SymbolId } from './types';

/**
 * Symbol catalogue. Default sizes are real-world metres so placed fixtures are
 * to scale with rooms/walls (see PIXELS_PER_METRE).
 *
 * Each family is one button in the panel. The first variation is the default.
 */
export interface SymbolDef {
  id: SymbolId;
  label: string;
  group: 'openings' | 'bedroom' | 'living' | 'kitchen' | 'bathroom' | 'circulation' | 'annotation';
  widthM: number;
  heightM: number;
}

export interface SymbolVariation {
  id: SymbolId;
  label: string;
  widthM: number;
  heightM: number;
}

export interface SymbolFamily {
  id: string;
  label: string;
  group: SymbolDef['group'];
  variations: SymbolVariation[];
}

export const SYMBOL_FAMILIES: SymbolFamily[] = [
  {
    id: 'door',
    label: 'Door',
    group: 'openings',
    variations: [
      { id: 'door', label: 'Standard', widthM: 0.85, heightM: 0.85 },
      { id: 'door-room', label: 'Bed / bath', widthM: 0.7, heightM: 0.7 },
      { id: 'door-cupboard', label: 'Pantry', widthM: 0.45, heightM: 0.45 },
      { id: 'double-door', label: 'Double', widthM: 1.6, heightM: 0.8 },
      { id: 'sliding-door', label: 'Sliding', widthM: 1.6, heightM: 0.22 },
    ],
  },
  {
    id: 'window',
    label: 'Window',
    group: 'openings',
    variations: [
      { id: 'window', label: 'Standard', widthM: 1.0, heightM: 0.18 },
      { id: 'window-sliding', label: 'Sliding', widthM: 1.4, heightM: 0.18 },
      { id: 'window-bay', label: 'Bay', widthM: 1.6, heightM: 0.7 },
    ],
  },
  {
    id: 'bed',
    label: 'Bed',
    group: 'bedroom',
    variations: [
      { id: 'bed-double', label: 'Double', widthM: 1.5, heightM: 2.0 },
      { id: 'bed-single', label: 'Single', widthM: 0.9, heightM: 1.9 },
    ],
  },
  {
    id: 'sofa',
    label: 'Sofa',
    group: 'living',
    variations: [
      { id: 'sofa', label: 'Three seat', widthM: 2.0, heightM: 0.9 },
      { id: 'sofa-two', label: 'Two seat', widthM: 1.4, heightM: 0.9 },
      { id: 'sofa-l', label: 'L-shape', widthM: 2.2, heightM: 1.8 },
    ],
  },
  {
    id: 'kitchen-sink',
    label: 'Kitchen sink',
    group: 'kitchen',
    variations: [
      { id: 'kitchen-sink', label: 'Drainer', widthM: 1.0, heightM: 0.5 },
      { id: 'kitchen-sink-double', label: 'Double', widthM: 0.8, heightM: 0.5 },
    ],
  },
  {
    id: 'range',
    label: 'Range',
    group: 'kitchen',
    variations: [
      { id: 'range', label: 'Cooker', widthM: 0.6, heightM: 0.6 },
      { id: 'range-wide', label: 'Wide', widthM: 0.9, heightM: 0.6 },
    ],
  },
  {
    id: 'sink',
    label: 'Sink',
    group: 'bathroom',
    variations: [
      { id: 'sink', label: 'Single', widthM: 0.6, heightM: 0.5 },
      { id: 'sink-double', label: 'Double', widthM: 1.2, heightM: 0.5 },
    ],
  },
  {
    id: 'toilet',
    label: 'Toilet',
    group: 'bathroom',
    variations: [
      { id: 'toilet', label: 'Close coupled', widthM: 0.45, heightM: 0.7 },
      { id: 'toilet-wall', label: 'Wall hung', widthM: 0.4, heightM: 0.55 },
    ],
  },
  {
    id: 'bath',
    label: 'Bathtub',
    group: 'bathroom',
    variations: [
      { id: 'bathtub', label: 'Bath', widthM: 1.7, heightM: 0.75 },
      { id: 'bathtub-shower', label: 'With shower', widthM: 1.7, heightM: 0.75 },
    ],
  },
  {
    id: 'shower',
    label: 'Shower',
    group: 'bathroom',
    variations: [
      { id: 'corner-shower', label: 'Corner', widthM: 0.9, heightM: 0.9 },
      { id: 'stall-shower', label: 'Stall', widthM: 0.9, heightM: 1.2 },
    ],
  },
  {
    id: 'stairs',
    label: 'Stairs',
    group: 'circulation',
    variations: [
      { id: 'stairs', label: 'Straight', widthM: 1.0, heightM: 3.0 },
      { id: 'stairs-l', label: 'L-shape', widthM: 1.8, heightM: 2.4 },
    ],
  },
  {
    id: 'north',
    label: 'North',
    group: 'annotation',
    variations: [
      { id: 'north', label: 'Point', widthM: 0.8, heightM: 1.1 },
      { id: 'north-arrow', label: 'Arrow', widthM: 0.7, heightM: 1.1 },
      { id: 'north-circle', label: 'Circle', widthM: 0.9, heightM: 0.9 },
      { id: 'north-ns', label: 'N & S', widthM: 0.7, heightM: 1.1 },
      { id: 'north-compass', label: 'Compass', widthM: 0.9, heightM: 0.9 },
    ],
  },
];

export const SYMBOLS: SymbolDef[] = SYMBOL_FAMILIES.flatMap((family) =>
  family.variations.map((variation) => ({ ...variation, group: family.group }))
);

export const SYMBOL_BY_ID: Record<SymbolId, SymbolDef> = SYMBOLS.reduce(
  (acc, s) => {
    acc[s.id] = s;
    return acc;
  },
  {} as Record<SymbolId, SymbolDef>
);

/** Plans saved while the north point was its own id still resolve to that drawing. */
SYMBOL_BY_ID['north-point'] = { ...SYMBOL_BY_ID.north, id: 'north-point' };

const LEGACY_SYMBOL: Partial<Record<SymbolId, SymbolId>> = {
  'north-point': 'north',
};

export function canonicalSymbolId(id: SymbolId): SymbolId {
  return LEGACY_SYMBOL[id] ?? id;
}

export function familyOf(id: SymbolId): SymbolFamily | undefined {
  const canonical = canonicalSymbolId(id);
  return SYMBOL_FAMILIES.find((family) => family.variations.some((variation) => variation.id === canonical));
}

export function symbolSupportsStairDirection(id: SymbolId): boolean {
  return id === 'stairs' || id === 'stairs-l';
}

/** Swing and double doors sit on the wall: the click is the opening, not the box centre. */
export function symbolAnchorsToWall(id: SymbolId): boolean {
  return id === 'door' || id === 'door-room' || id === 'door-cupboard' || id === 'double-door';
}

/** Doors and kitchen/bathroom fixtures can be mirrored. North marks stay as drawn. */
export function symbolSupportsFlip(id: SymbolId): boolean {
  const family = familyOf(id);
  if (!family) return false;
  return family.group === 'openings' || family.group === 'kitchen' || family.group === 'bathroom';
}

export const SYMBOL_GROUPS: { id: SymbolDef['group']; label: string }[] = [
  { id: 'openings', label: 'Doors & windows' },
  { id: 'bedroom', label: 'Bedroom' },
  { id: 'living', label: 'Living' },
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'bathroom', label: 'Bathroom' },
  { id: 'circulation', label: 'Circulation' },
  { id: 'annotation', label: 'Annotation' },
];
